import { useNavigate } from "react-router-dom";
// src/pages/TestLabPage.jsx — Phase 4 / VIRAJ Test & Replay Lab
//
// UI дашборд для Validation, Replay & Evidence Layer v1.0
//
// Использует ТОЛЬКО /api/test/* — никаких production API вызовов.
// Никаких backend изменений. Никакого ML.
//
// Блоки:
//   [1] Test Campaign & Crisis Panel
//   [2] Engine & Replay Panel
//   [3] Evidence & Narrative Panel
//   [4] Stats & Maintenance Panel

import { useState, useEffect, useCallback } from "react";
import { workerClient, BASE_URL }           from "../api/workerClient";

// ── Style helpers ─────────────────────────────────────────
const MONO  = "var(--font-mono)";
const LABEL = { fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6 };
const VALUE = { fontFamily: MONO, fontSize: 13, color: "var(--text-primary)", fontWeight: 700 };
const BTN   = (accent) => ({
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "8px 16px", borderRadius: "var(--radius-sm)", cursor: "pointer",
  fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
  textTransform: "uppercase", border: "none",
  background: accent ? "var(--accent-primary)" : "var(--bg-elevated)",
  color: accent ? "#fff" : "var(--text-secondary)",
  transition: "opacity 0.15s",
});

const RISK_COLOR = { low: "var(--success)", medium: "var(--warning)", high: "var(--danger)", critical: "#ff3333", unknown: "var(--text-muted)" };

function SectionLabel({ children }) {
  return <div style={LABEL}>{children}</div>;
}
function Block({ title, children, accent }) {
  return (
    <div style={{
      padding: "18px 20px", borderRadius: "var(--radius)",
      background: "var(--bg-card)",
      border: `1px solid ${accent ? "var(--border-accent)" : "var(--border-subtle)"}`,
    }}>
      <div style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: accent ? "var(--accent-primary)" : "var(--text-muted)",
                    marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
        {accent && <span style={{ color: "var(--accent-primary)" }}>◈</span>}
        {title}
      </div>
      {children}
    </div>
  );
}
function ErrorBanner({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ padding: "8px 14px", borderRadius: "var(--radius-sm)",
                  background: "var(--danger-bg)", border: "1px solid var(--danger-border)",
                  fontFamily: MONO, fontSize: 11, color: "var(--danger)", marginTop: 10 }}>
      ⚠ {msg}
    </div>
  );
}
function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ padding: "8px 14px", borderRadius: "var(--radius-sm)",
                  background: "var(--success-bg)", border: "1px solid var(--success-border)",
                  fontFamily: MONO, fontSize: 11, color: "var(--success)", marginTop: 10 }}>
      ✓ {msg}
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <SectionLabel>{label}</SectionLabel>
      <select
        value={value} onChange={e => onChange(e.target.value)}
        style={{ fontFamily: MONO, fontSize: 11, background: "var(--bg-base)",
                  border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-sm)",
                  padding: "6px 10px", color: "var(--text-primary)", width: "100%" }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
function NumInput({ label, value, onChange, min, max, step }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <SectionLabel>{label}</SectionLabel>
      <input type="number" value={value} onChange={e => onChange(Number(e.target.value))}
        min={min} max={max} step={step}
        style={{ fontFamily: MONO, fontSize: 11, background: "var(--bg-base)",
                  border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-sm)",
                  padding: "6px 10px", color: "var(--text-primary)", width: "100%" }}
      />
    </div>
  );
}
function MiniCard({ label, value, color }) {
  return (
    <div style={{ padding: "8px 12px", borderRadius: "var(--radius-sm)",
                  background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)" }}>
      <div style={{ ...LABEL, marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: color ?? "var(--text-primary)" }}>{value ?? "—"}</div>
    </div>
  );
}

// ── postTest helper ───────────────────────────────────────
async function postTest(path, body) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.json();
}
async function deleteTest(path) {
  const res = await fetch(`${BASE_URL}${path}`, { method: "DELETE" });
  return res.json();
}

// ── Panel 1: Test Campaign & Crisis ──────────────────────
function CampaignPanel({ campaignId, onCampaignCreated, onCrisis }) {
  const [scenario,   setScenario]   = useState("crisis");
  const [days,       setDays]       = useState(14);
  const [crisisType, setCrisisType] = useState("crash");
  const [severity,   setSeverity]   = useState(0.8);
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState("");
  const [error,      setError]      = useState("");
  const [crisisRes,  setCrisisRes]  = useState(null);

  async function createCampaign() {
    setLoading(true); setError(""); setToast("");
    try {
      const res = await postTest("/api/test/campaign", { scenario, days });
      if (res.ok) { onCampaignCreated(res.campaign_id); setToast(`Test campaign created: ${res.campaign_id}`); }
      else         setError(res.error ?? "Failed to create campaign");
    } catch (e) { setError(String(e)); }
    setLoading(false);
  }

  async function simulateCrisis() {
    if (!campaignId) { setError("Create a test campaign first"); return; }
    setLoading(true); setError(""); setToast("");
    try {
      const res = await postTest("/api/test/simulate/crisis", { campaign_id: campaignId, type: crisisType, severity });
      if (res.ok) { setCrisisRes(res); setToast(`Crisis injected: ${res.events_modified} events modified`); onCrisis(res); }
      else         setError(res.error ?? "Failed to simulate crisis");
    } catch (e) { setError(String(e)); }
    setLoading(false);
  }

  return (
    <Block title="Test Campaign & Crisis" accent>
      <Select label="Scenario" value={scenario} onChange={setScenario}
        options={[{value:"crisis",label:"Crisis (CR crash)"},{value:"normal",label:"Normal baseline"},
                  {value:"spend_spike",label:"Spend spike"},{value:"attribution_delay",label:"Attribution delay"}]} />
      <NumInput label="Days" value={days} onChange={setDays} min={7} max={30} step={1} />

      <button style={BTN(true)} onClick={createCampaign} disabled={loading}>
        {loading ? "Creating…" : "Create Test Campaign"}
      </button>

      {campaignId && (
        <div style={{ marginTop: 10, fontFamily: MONO, fontSize: 10, color: "var(--success)" }}>
          ✓ Active: <span style={{ fontWeight: 700 }}>{campaignId}</span>
        </div>
      )}

      <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border-subtle)" }}>
        <SectionLabel>Inject Crisis</SectionLabel>
        <Select label="Crisis type" value={crisisType} onChange={setCrisisType}
          options={[{value:"crash",label:"Crash (CR collapse)"},{value:"spend_spike",label:"Spend spike"},
                    {value:"attribution_delay",label:"Attribution delay"},{value:"stale_baseline",label:"Stale baseline"}]} />
        <div style={{ marginBottom: 10 }}>
          <SectionLabel>Severity: {severity}</SectionLabel>
          <input type="range" min="0.1" max="0.9" step="0.1" value={severity}
            onChange={e => setSeverity(Number(e.target.value))}
            style={{ width: "100%" }} />
        </div>
        <button style={BTN(false)} onClick={simulateCrisis} disabled={loading || !campaignId}>
          Simulate Crisis
        </button>
      </div>

      {crisisRes && (
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <MiniCard label="Crisis type"     value={crisisRes.crisis_type} />
          <MiniCard label="Expected risk"   value={crisisRes.expected_risk?.toUpperCase()} color={RISK_COLOR[crisisRes.expected_risk]} />
          <MiniCard label="Events modified" value={crisisRes.events_modified} />
          <MiniCard label="Expected action" value={crisisRes.expected_action?.toUpperCase()} />
        </div>
      )}

      <Toast msg={toast} />
      <ErrorBanner msg={error} />
    </Block>
  );
}

// ── Panel 2: Engine & Replay ──────────────────────────────
function EngineReplayPanel({ campaignId }) {
  const [dryRun,  setDryRun]  = useState(null);
  const [replay,  setReplay]  = useState(null);
  const [mode,    setMode]    = useState("balanced");
  const [loading, setLoading] = useState("");
  const [error,   setError]   = useState("");

  async function runDryRun() {
    if (!campaignId) { setError("Create a test campaign first"); return; }
    setLoading("dryrun"); setError("");
    try {
      const res = await postTest("/api/test/engine/dry_run", { campaign_id: campaignId, mode });
      if (res.ok) setDryRun(res);
      else        setError(res.error ?? "Dry-run failed");
    } catch (e) { setError(String(e)); }
    setLoading("");
  }

  async function runReplay() {
    if (!campaignId) { setError("Create a test campaign first"); return; }
    setLoading("replay"); setError("");
    try {
      const res = await postTest("/api/test/replay", { campaign_id: campaignId });
      if (res.ok) setReplay(res);
      else        setError(res.error ?? "Replay failed");
    } catch (e) { setError(String(e)); }
    setLoading("");
  }

  return (
    <Block title="Engine & Replay">
      <Select label="Engine mode" value={mode} onChange={setMode}
        options={[{value:"stable",label:"Stable"},{value:"balanced",label:"Balanced"},{value:"aggressive",label:"Aggressive"}]} />

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button style={BTN(true)} onClick={runDryRun} disabled={!!loading}>
          {loading === "dryrun" ? "Running…" : "Run Engine Dry-Run"}
        </button>
        <button style={BTN(false)} onClick={runReplay} disabled={!!loading}>
          {loading === "replay" ? "Running…" : "Replay Timeline"}
        </button>
      </div>

      {dryRun && (
        <div style={{ marginBottom: 14 }}>
          <SectionLabel>Dry-Run Result</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px,1fr))", gap: 8 }}>
            <MiniCard label="Risk status"  value={dryRun.risk?.risk_status?.toUpperCase()} color={RISK_COLOR[dryRun.risk?.risk_status]} />
            <MiniCard label="Risk score"   value={dryRun.risk?.risk_score?.toFixed(3)} />
            <MiniCard label="Action"       value={dryRun.action?.toUpperCase()} />
            <MiniCard label="Root cause"   value={dryRun.attribution?.root_cause} />
          </div>
          {dryRun.risk?.reasons?.length > 0 && (
            <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: "var(--radius-sm)",
                          background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)" }}>
              {dryRun.risk.reasons.map((r, i) => (
                <div key={i} style={{ fontFamily: MONO, fontSize: 10, color: "var(--text-secondary)", marginBottom: 3 }}>
                  ↓ {r}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {replay && (
        <div>
          <SectionLabel>Replay Timeline</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 10 }}>
            <MiniCard label="Days total"   value={replay.days_total} />
            <MiniCard label="Anomaly days" value={replay.anomaly_days} color="var(--danger)" />
            <MiniCard label="Peak risk"    value={replay.peak_risk_score} />
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 10 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                  {["Date","Step","Spend","Revenue","CR","Risk","Anomaly"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "4px 8px", color: "var(--text-muted)",
                                        fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {replay.timeline?.map((t, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border-subtle)",
                                       background: t.anomaly ? "rgba(232,85,85,0.06)" : "transparent" }}>
                    <td style={{ padding: "4px 8px", color: "var(--text-muted)" }}>{t.date}</td>
                    <td style={{ padding: "4px 8px", color: t.anomaly ? "var(--danger)" : "var(--text-secondary)", fontWeight: t.anomaly ? 700 : 400 }}>{t.step}</td>
                    <td style={{ padding: "4px 8px", color: "var(--text-secondary)" }}>{t.spend}</td>
                    <td style={{ padding: "4px 8px", color: "var(--text-secondary)" }}>{t.revenue}</td>
                    <td style={{ padding: "4px 8px", color: "var(--text-secondary)" }}>{t.cr?.toFixed(4)}</td>
                    <td style={{ padding: "4px 8px", color: RISK_COLOR[t.risk] ?? "var(--text-muted)" }}>{t.risk?.toUpperCase()}</td>
                    <td style={{ padding: "4px 8px", color: t.anomaly ? "var(--danger)" : "var(--text-muted)" }}>{t.anomaly ? "⚠ yes" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ErrorBanner msg={error} />
    </Block>
  );
}

// ── Panel 3: Evidence & Narrative ────────────────────────
function EvidencePanel({ campaignId }) {
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  async function generateReport() {
    if (!campaignId) { setError("Create a test campaign first"); return; }
    setLoading(true); setError("");
    try {
      const res = await workerClient(`/api/test/report?campaign_id=${campaignId}`);
      if (res.ok) setReport(res);
      else        setError(res.error ?? "Report generation failed");
    } catch (e) { setError(String(e)); }
    setLoading(false);
  }

  return (
    <Block title="Evidence & Narrative">
      <button style={BTN(true)} onClick={generateReport} disabled={loading}>
        {loading ? "Generating…" : "Generate Full Report"}
      </button>

      {report && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Risk + Action summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px,1fr))", gap: 8 }}>
            <MiniCard label="Risk status"   value={report.risk?.status?.toUpperCase()}    color={RISK_COLOR[report.risk?.status]} />
            <MiniCard label="Action taken"  value={report.action?.toUpperCase()} />
            <MiniCard label="Anomaly days"  value={report.stats?.anomaly_days}            color="var(--danger)" />
            <MiniCard label="CR drop"       value={`−${report.stats?.cr_drop_pct ?? 0}%`} color="var(--danger)" />
            <MiniCard label="Prevented est" value={report.prevented_loss_estimate > 0 ? `€ ${report.prevented_loss_estimate}` : "€ 0"} color="var(--success)" />
          </div>

          {/* Evidence Chain */}
          {report.evidence && (
            <div style={{ padding: "12px 16px", borderRadius: "var(--radius-sm)",
                          background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)" }}>
              <div style={{ ...LABEL, marginBottom: 8 }}>Evidence Chain</div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: "var(--accent-primary)",
                            fontWeight: 700, marginBottom: 8 }}>
                Root cause: {report.evidence.root_cause}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 9, color: "var(--text-muted)",
                            marginBottom: 10, fontStyle: "italic" }}>
                {report.evidence.chain_logic}
              </div>
              {report.evidence.evidence?.map((e, i) => (
                <div key={i} style={{ display: "flex", gap: 7, marginBottom: 5 }}>
                  <span style={{ color: "var(--accent-primary)", flexShrink: 0 }}>◈</span>
                  <span style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.55 }}>{e}</span>
                </div>
              ))}
            </div>
          )}

          {/* Narrative */}
          {report.narrative && (
            <div style={{ padding: "12px 16px", borderRadius: "var(--radius-sm)",
                          background: "rgba(18,204,118,0.05)", border: "1px solid rgba(18,204,118,0.18)" }}>
              <div style={{ ...LABEL, marginBottom: 6, color: "var(--success)" }}>Narrative</div>
              <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700,
                            color: "var(--text-primary)", lineHeight: 1.6, marginBottom: 8 }}>
                {report.narrative.summary}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.65 }}>
                {report.narrative.details}
              </div>
              <div style={{ marginTop: 8, fontFamily: MONO, fontSize: 9, color: "var(--text-muted)" }}>
                Template: {report.narrative.template_used}
              </div>
            </div>
          )}

          {/* Attribution contributors */}
          {report.attribution?.contributors?.length > 0 && (
            <div>
              <div style={LABEL}>Signal contributors</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {report.attribution.contributors.map((c, i) => (
                  <div key={i} style={{
                    padding: "4px 10px", borderRadius: "var(--radius-sm)",
                    background: c.delta < 0 ? "var(--danger-bg)" : "var(--success-bg)",
                    border: `1px solid ${c.delta < 0 ? "var(--danger-border)" : "var(--success-border)"}`,
                    fontFamily: MONO, fontSize: 10, fontWeight: 700,
                    color: c.delta < 0 ? "var(--danger)" : "var(--success)",
                  }}>
                    {c.metric}: {c.delta > 0 ? "+" : ""}{c.delta}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <ErrorBanner msg={error} />
    </Block>
  );
}

// ── Panel 4: Stats & Maintenance ─────────────────────────
function StatsPanel({ onCleared }) {
  const [status,  setStatus]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState("");
  const [error,   setError]   = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const res = await workerClient("/api/test/status");
      if (res.ok) setStatus(res);
    } catch {}
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function clearData() {
    if (!window.confirm("Delete ALL test data (is_test=1)? Production data will NOT be affected.")) return;
    setLoading(true); setError(""); setToast("");
    try {
      const res = await deleteTest("/api/test/clear");
      if (res.ok) {
        setToast(`Cleared: ${res.total} test rows deleted`);
        await loadStatus();
        onCleared();
      } else setError(res.error ?? "Clear failed");
    } catch (e) { setError(String(e)); }
    setLoading(false);
  }

  return (
    <Block title="Stats & Maintenance">
      <SectionLabel>Test Layer Status</SectionLabel>

      {status ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px,1fr))", gap: 8, marginBottom: 14 }}>
          <MiniCard label="Campaigns"     value={status.test_rows?.campaigns     ?? "—"} />
          <MiniCard label="Fin. logs"     value={status.test_rows?.financial_logs ?? "—"} />
          <MiniCard label="Auto actions"  value={status.test_rows?.auto_actions  ?? "—"} />
          <MiniCard label="Engine events" value={status.test_rows?.engine_events ?? "—"} />
        </div>
      ) : (
        <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-muted)", marginBottom: 14 }}>
          {status === null ? "Loading…" : "Migration not applied yet (run 004_test_mode.sql)"}
        </div>
      )}

      {status?.migration_ok === false && (
        <div style={{ padding: "8px 12px", borderRadius: "var(--radius-sm)",
                      background: "var(--warning-bg)", border: "1px solid var(--warning-border)",
                      fontFamily: MONO, fontSize: 10, color: "var(--warning)", marginBottom: 12 }}>
          ⚠ Migration not applied. Run: wrangler d1 execute viraj-db --remote --file="backend/migrations/004_test_mode.sql"
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button style={BTN(false)} onClick={loadStatus}>↻ Refresh</button>
        <button style={{ ...BTN(false), color: "var(--danger)", borderColor: "var(--danger-border)" }}
          onClick={clearData} disabled={loading}>
          {loading ? "Clearing…" : "Clear Test Data"}
        </button>
      </div>

      <Toast msg={toast} />
      <ErrorBanner msg={error} />

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-subtle)",
                    fontFamily: MONO, fontSize: 9, color: "var(--text-muted)", lineHeight: 1.7 }}>
        All test data is isolated via <code style={{ color: "var(--accent-primary)" }}>is_test = 1</code>.
        Production data ({" "}<code style={{ color: "var(--success)" }}>is_test = 0</code>){" "}
        is never affected by any operation in this panel.
      </div>
    </Block>
  );
}

// ── TestLabPage ───────────────────────────────────────────
export default function TestLabPage() {
  const navigate = useNavigate();
  const [campaignId, setCampaignId] = useState("");

  function handleCleared() {
    setCampaignId(""); // reset after clear
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Return to Analyze */}
      <div data-no-print style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
        <button
          onClick={() => navigate("/analyze")}
          style={{
            fontFamily: "var(--font-mono)", fontSize: 10, cursor: "pointer",
            padding: "4px 10px", borderRadius: "var(--radius-sm)",
            background: "transparent", border: "1px solid var(--border-subtle)",
            color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5,
          }}
        >
          ← Analyze
        </button>
      </div>

      {/* Header */}
      <div style={{ paddingBottom: 16, borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 18 }}>⚗</span>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>
              VIRAJ Test & Replay Lab
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: "var(--text-muted)", marginTop: 3 }}>
              Validation, Replay & Evidence Layer v1.0 · isolated test mode · no production data
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6,
                        padding: "4px 10px", borderRadius: "var(--radius-sm)",
                        background: "rgba(18,204,118,0.07)", border: "1px solid rgba(18,204,118,0.2)" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--success)" }} />
            <span style={{ fontFamily: MONO, fontSize: 9, color: "var(--success)", fontWeight: 700, letterSpacing: "0.1em" }}>
              TEST MODE
            </span>
          </div>
        </div>
      </div>

      {/* Campaign ID badge */}
      {campaignId && (
        <div style={{ padding: "8px 14px", borderRadius: "var(--radius-sm)",
                      background: "var(--accent-faint)", border: "1px solid var(--border-accent)",
                      fontFamily: MONO, fontSize: 10, color: "var(--accent-primary)" }}>
          Active campaign: <strong>{campaignId}</strong>
        </div>
      )}

      {/* 4 Panels */}
      <CampaignPanel
        campaignId={campaignId}
        onCampaignCreated={id => setCampaignId(id)}
        onCrisis={() => {}}
      />
      <EngineReplayPanel campaignId={campaignId} />
      <EvidencePanel    campaignId={campaignId} />
      <StatsPanel       onCleared={handleCleared} />

    </div>
  );
}


